import React, { FC } from 'react';
import IgBadge from '@components/EntityDrawers/components/ReferencesInputsGroup/components/IgBadge';
import { UnderlineTextButton } from '@components/UnderlineTextButton';
import { ImplementationGroup } from '@constants/entities/project';
import { Divider, Typography } from '@mui/material';
import {
  useModelFilterActions,
  useModelFilters,
} from '@store/zustand/model-filters';
import {
  CisCatalogue,
  CisReference,
  controlValueIds,
  countSelected,
  safeguardsOfTier,
  searchCisReferences,
  selectionCoverage,
  toggleAll,
} from '@utils/cisCatalogue';
import { CATEGORY_LABELS } from '@views/Project/components/ModelFiltersPopover/categories';
import FilterCisPillRow from '@views/Project/components/ModelFiltersPopover/components/FilterCisPillRow';
import FilterCisSearchField from '@views/Project/components/ModelFiltersPopover/components/FilterCisSearchField';
import FilterNavRow from '@views/Project/components/ModelFiltersPopover/components/FilterNavRow';
import FilterPanelBackTitle from '@views/Project/components/ModelFiltersPopover/components/FilterPanelBackTitle';
import FilterPanelLayout from '@views/Project/components/ModelFiltersPopover/components/FilterPanelLayout';
import FilterValueRow from '@views/Project/components/ModelFiltersPopover/components/FilterValueRow';
import { useSetCategory } from '@views/Project/components/ModelFiltersPopover/useSetCategory';

export const CIS_SELECT_ALL_LABEL = 'Select all';

export const CIS_NO_RESULT_TEXT = 'No result';

/**
 * AC5.1.1's hint under an empty search field, naming the catalogue's
 * size — Controls and Safeguards combined.
 */
export const cisSearchHint = (total: number): string =>
  `Start typing to search all ${total} references.`;

type Props = {
  catalogue: CisCatalogue;
  counts?: Record<string, number>;
  pill: ImplementationGroup;
  activeControlId?: string;
  query: string | null;
  onPillChange: (pill: ImplementationGroup) => void;
  onOpenControl: (controlId: string) => void;
  onQueryChange: (query: string | null) => void;
  onBack: () => void;
};

/**
 * The CIS-18 reference category panel: the header, then the pill row — or
 * the search field in its place — pinned above the list the active pill
 * shows (AC1.2).
 *
 * The selection is the store's `cis` list of catalogue ids, written
 * through `useSetCategory` like any other category, so a tick applies at
 * once and closes a drawer it would hide (AC6.1). An IGn pill lists the
 * Safeguards whose entry tier is that group, in catalogue order, under a
 * Select all that reaches those Safeguards alone: it reads checked once all
 * of them are selected, indeterminate for some, and a click selects the
 * rest or, from all, unselects them (AC2.3.1). Those rows carry no IG
 * badge, every one being the pill's tier; the badge appears where tiers
 * mix, under a Control and among search results. The Controls pill lists
 * Controls as navigation rows: no checkbox, no Select all, no count, a
 * chevron, and a badge with the number of the Control's values — itself
 * and its Safeguards — currently selected (AC3.1, AC3.4); a row click is
 * reported, and the container opens, swaps or closes the side panel from
 * it, marking the open row through `activeControlId`.
 * Search is a case-insensitive contains-match over ids and titles from the
 * first character (AC5.2, AC5.3): a blank query shows the hint, no match
 * "No result", and matches form one flat selectable list in catalogue
 * order, Safeguards badged, Controls not (AC5.4). Counts are rendered
 * only when the container supplies them, and only on selectable rows; a
 * value missing from the map reads 0. "Clear all" appears while the
 * category has a selection and empties it alone (AC7.2). The panel owns
 * no navigation state: which pill is active, which Control is open and
 * what is being searched live in the popover store, so a chip reopen
 * lands where the store says.
 */
const FilterCisPanel: FC<Props> = ({
  catalogue,
  counts,
  pill,
  activeControlId,
  query,
  onPillChange,
  onOpenControl,
  onQueryChange,
  onBack,
}) => {
  const { cis: selected } = useModelFilters();
  const { clearCategory } = useModelFilterActions();
  const setCategory = useSetCategory();
  const total = catalogue.references.length;

  const toggle = (id: string) =>
    setCategory(
      'cis',
      selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id],
    );

  const renderReferenceRow = (reference: CisReference, badged: boolean) => (
    <FilterValueRow
      key={reference.id}
      label={reference.label}
      checked={selected.includes(reference.id)}
      count={counts ? counts[reference.id] ?? 0 : undefined}
      endAdornment={
        badged && reference.tier ? <IgBadge tier={reference.tier} /> : undefined
      }
      onToggle={() => toggle(reference.id)}
    />
  );

  const renderSelectAll = (ids: string[]) => {
    const coverage = selectionCoverage(ids, selected);

    return (
      <FilterValueRow
        label={CIS_SELECT_ALL_LABEL}
        checked={coverage === 'all'}
        indeterminate={coverage === 'some'}
        onToggle={() => setCategory('cis', toggleAll(selected, ids))}
      />
    );
  };

  const renderMessage = (text: string) => (
    <Typography variant="body2" color="grey.700" sx={{ p: '12px' }}>
      {text}
    </Typography>
  );

  const renderSearchResults = (needle: string) => {
    if (!needle.trim()) return renderMessage(cisSearchHint(total));

    const results = searchCisReferences(catalogue, needle);

    if (!results.length) return renderMessage(CIS_NO_RESULT_TEXT);

    return results.map((reference) => renderReferenceRow(reference, true));
  };

  const renderPillList = () => {
    if (pill === ImplementationGroup.Controls) {
      return catalogue.controls.map((control) => (
        <FilterNavRow
          key={control.id}
          label={control.label}
          badge={countSelected(controlValueIds(control), selected)}
          selected={control.id === activeControlId}
          onClick={() => onOpenControl(control.id)}
        />
      ));
    }

    const safeguards = safeguardsOfTier(catalogue, pill);

    return (
      <>
        {renderSelectAll(safeguards.map(({ id }) => id))}
        <Divider sx={{ borderColor: 'grey.500' }} />
        {safeguards.map((safeguard) => renderReferenceRow(safeguard, false))}
      </>
    );
  };

  return (
    <FilterPanelLayout
      title={
        <FilterPanelBackTitle title={CATEGORY_LABELS.cis} onBack={onBack} />
      }
      action={
        selected.length > 0 && (
          <UnderlineTextButton
            $disabled={false}
            onClick={() => clearCategory('cis')}
            sx={{ flexShrink: 0 }}
          >
            Clear all
          </UnderlineTextButton>
        )
      }
      toolbar={
        query === null ? (
          <FilterCisPillRow
            pill={pill}
            onPillChange={onPillChange}
            onSearch={() => onQueryChange('')}
          />
        ) : (
          <FilterCisSearchField
            query={query}
            total={total}
            onChange={onQueryChange}
            onExit={() => onQueryChange(null)}
          />
        )
      }
    >
      {query === null ? renderPillList() : renderSearchResults(query)}
    </FilterPanelLayout>
  );
};

export default FilterCisPanel;
